import { useCallback, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiChip,
  MiIcon,
  MiIconButton,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  PERMISSIONS,
  PROPERTY_TYPE,
} from "@miplatform/constants";
import { useFeatureFlagsContext } from "@miplatform/feature-flags";
import { useBoolean, useRouter } from "@miplatform/hooks";
import {
  AutoAssignWorkOrderTemplateStatus,
  CalendarEventType,
  CalendarType,
  Colors,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
} from "@miplatform/types";
import { mdiCalendarBlankOutline, mdiPlus, mdiPrinterOutline } from "@mdi/js";
import {
  OrderActivityDrawer,
  SignalRConnectionIndicator,
} from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { useDetailedCalendarSet } from "../../hooks/useCalendarSet/useDetailedCalendarSet";
import { CalendarProvider, useCalendarContext } from "../../providers";
import { useStoredCalendarSet } from "../../stores";
import { OrdersProvider, useOrdersContext } from "../Orders/OrdersProvider";
import { CreateNewOrderDrawer } from "../Schedule/Scheduler/CreateNewOrderDrawer/CreateNewOrderDrawer";
import { CalendarAttemptSearch } from "./CalendarAttemptSearch/CalendarAttemptSearch";
import { CalendarRenderer } from "./CalendarRenderer";
import { HoldsPrintModal } from "./HoldsPrintModal/HoldsPrintModal";
import { NhcBoardHeaderCenter } from "./NhcBoardHeaderCenter";
import { useAutoAssignStatusIndicator } from "./useAutoAssignStatusIndicator";
import { useNhcBoardViewModel } from "./useNhcBoardViewModel";

const TypedOrderActivityDrawer =
  OrderActivityDrawer as React.ComponentType<any>;

export const NhcBoard = () => {
  const { params } = useRouter();
  const { calendarSetId } = params;
  useDetailedCalendarSet(calendarSetId);

  return (
    <OrdersProvider>
      <CalendarProvider>
        <NhcBoardContent />
      </CalendarProvider>
    </OrdersProvider>
  );
};

const NhcBoardContent = () => {
  const { classes, cx } = useStyles();
  const { params } = useRouter();
  const { calendarSetId, calendarId } = params;
  const { featureFlags } = useFeatureFlagsContext();
  const { calendarSet } = useStoredCalendarSet(calendarSetId ?? null);
  const { config: calendarSetConfig } = calendarSet ?? {};

  const {
    setSelectedOrder,
    selectedOrder,
    orderDetails,
    getOrderById,
    clearOrderDetails,
    refreshOrder,
    getOrderActivity,
    handleTabChange,
    isCreateNewOrderDrawerOpen,
    setIsCreateNewOrderDrawerOpen,
    handleAfterOrderCreation,
  } = useNhcBoardViewModel();
  const { isOrderActivityDrawerOpen, openDrawer, closeDrawer } =
    useOrdersContext();

  const handleSelectOrder = useCallback(
    (
      order:
        | IAttemptListResponse
        | IAttemptListItemResponseForWorkOrder
        | undefined
    ) => {
      setSelectedOrder(order);
      if (order) {
        openDrawer();
      } else {
        closeDrawer();
        clearOrderDetails();
      }
    },
    [setSelectedOrder, openDrawer, closeDrawer, clearOrderDetails]
  );

  const handleOrderActivityDrawerClose = useCallback(() => {
    closeDrawer();
    clearOrderDetails();
  }, [closeDrawer, clearOrderDetails]);

  const {
    calendarConfig,
    calendarCounts,
    handleCreateWorkOrderDrawerOpen,
    autoAssignStatusUpdates,
  } = useCalendarContext();
  const {
    autoAssignIndicatorLabel,
    latestAutoAssignStatus,
    shouldLoadAutoAssignStatus,
  } = useAutoAssignStatusIndicator({
    calendarSetId: calendarSetConfig?.id,
    eventType: calendarSetConfig?.eventType,
    autoAssignStatusUpdates,
  });

  const canRenderCalendar = useMemo(() => {
    if (!calendarConfig || !calendarSetId) {
      return false;
    }

    if (calendarSet?.config.eventType === CalendarEventType.WorkOrder) {
      return Boolean(featureFlags?.advancedWorkOrdersEnabled);
    }

    return true;
  }, [calendarSet, calendarConfig, calendarSetId, featureFlags]);

  const tabs = useMemo(
    () =>
      calendarSetConfig?.calendarConfigs
        ?.map((calendar) => ({
          id: calendar.id,
          value: calendar.id,
          label: calendar.name,
          displayOrder: calendar.displayOrder,
          count: calendarCounts[calendar.id] || 0,
          ...(calendarCounts[calendar.id]
            ? { chipLabel: calendarCounts[calendar.id], chipColor: Colors.Red }
            : {}),
        }))
        .sort((a, b) => a.displayOrder - b.displayOrder) || [],
    [calendarSetConfig, calendarCounts, calendarSetId]
  );

  useEffect(() => {
    if (selectedOrder?.assignment?.order?.orderId) {
      getOrderById(selectedOrder.assignment.order.orderId);
    }
  }, [selectedOrder]);

  useEffect(() => {
    setIsCreateNewOrderDrawerOpen(false);
  }, [calendarSet]);

  if (!canRenderCalendar) {
    return null;
  }

  return (
    <>
      <NhcBoardHeader
        key={calendarId}
        onCreateNewOrderClick={() => setIsCreateNewOrderDrawerOpen(true)}
        onCreateNewWorkOrderClick={handleCreateWorkOrderDrawerOpen}
      />
      <Box className={classes.calendarTabsRow}>
        <Box className={classes.calendarTabs}>
          <MiTab
            tabs={tabs}
            currentTab={calendarConfig?.id}
            onTabChange={handleTabChange}
          />
        </Box>
        {shouldLoadAutoAssignStatus && (
          <MiChip
            label={autoAssignIndicatorLabel}
            size="small"
            color={Colors.Secondary}
            className={cx(classes.autoAssignIndicator, {
              [classes.autoAssignIndicatorRunning]:
                latestAutoAssignStatus?.status ===
                AutoAssignWorkOrderTemplateStatus.InProgress,
              [classes.autoAssignIndicatorCompleted]:
                latestAutoAssignStatus?.status ===
                AutoAssignWorkOrderTemplateStatus.Completed,
            })}
          />
        )}
      </Box>
      <Box display="flex" flexDirection="row" flex={1} overflow="hidden">
        <MiBox className={classes.orderBoardContainer}>
          <CalendarRenderer
            key={calendarSetId}
            setSelectedOrder={handleSelectOrder}
          />
        </MiBox>

        <TypedOrderActivityDrawer
          open={isOrderActivityDrawerOpen}
          onClose={handleOrderActivityDrawerClose}
          order={orderDetails}
          getOrderActivity={getOrderActivity}
          onRefresh={refreshOrder}
        />

        {isCreateNewOrderDrawerOpen && (
          <CreateNewOrderDrawer
            open={isCreateNewOrderDrawerOpen}
            onClose={handleAfterOrderCreation}
            defaultPropertyType={PROPERTY_TYPE.NEW_HOME_CONSTRUCTION}
          />
        )}
      </Box>
    </>
  );
};

const NhcBoardHeader = ({
  onCreateNewOrderClick,
  onCreateNewWorkOrderClick,
}: {
  onCreateNewOrderClick: () => void;
  onCreateNewWorkOrderClick: () => void;
}) => {
  const { classes, theme } = useStyles();
  const [isPrintModalOpen, handlePrintModalOpen, handlePrintModalClose] =
    useBoolean();
  const { hasPermission } = usePermissions();

  const {
    calendarConfig,
    calendarSet,
    search,
    connectionStatus,
    calendarCounts,
    handleCalendarSearch,
  } = useCalendarContext();
  const isPrintDisabled = useMemo(
    () =>
      parseInt(calendarCounts[calendarConfig?.id as string] ?? "0", 10) === 0,
    [calendarCounts, calendarConfig?.id]
  );

  return (
    <MiBuildPageHeader
      title="New Home Construction"
      search={search}
      onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleCalendarSearch(e.target.value)
      }
      centerComponent={<NhcBoardHeaderCenter />}
      leftComponent={
        <SignalRConnectionIndicator connectionStatus={connectionStatus} />
      }
      searchComponent={
        calendarConfig?.calendarType === CalendarType.PriorityScheduler ? (
          <CalendarAttemptSearch />
        ) : null
      }
      titleIcon={
        <MiIcon
          path={mdiCalendarBlankOutline}
          size={1}
          color={theme.palette.primary.main}
        />
      }
      actionComponent={
        <>
          {calendarSet?.config.eventType === CalendarEventType.Order &&
            hasPermission(PERMISSIONS.ORDER.CREATE) && (
              <MiButton
                title="Create New"
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={{
                  path: mdiPlus,
                }}
                onClick={onCreateNewOrderClick}
              />
            )}
          {calendarSet?.config.eventType === CalendarEventType.WorkOrder &&
            calendarConfig?.calendarType === CalendarType.PriorityScheduler &&
            hasPermission(PERMISSIONS.WORK_ORDER.CREATE) && (
              <MiButton
                title="Create New"
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={{
                  path: mdiPlus,
                }}
                onClick={onCreateNewWorkOrderClick}
              />
            )}
          {calendarSet?.config.eventType === CalendarEventType.WorkOrder &&
            (calendarConfig?.calendarType === CalendarType.HoldsList ||
              calendarConfig?.calendarType === CalendarType.AutoHold) && (
              <>
                <MiIconButton
                  data-testid="mi-holds-print-icon-button"
                  disabled={isPrintDisabled}
                  className={classes.printButton}
                  onClick={handlePrintModalOpen}
                >
                  <MiIcon path={mdiPrinterOutline} />
                </MiIconButton>
                {!isPrintDisabled && (
                  <HoldsPrintModal
                    open={isPrintModalOpen}
                    calendarId={calendarConfig?.id}
                    templatesIds={
                      calendarConfig?.workTemplateBases?.map(
                        (template) => template.id
                      ) ?? []
                    }
                    eventType={calendarSet?.config.eventType}
                    calendarType={calendarConfig?.calendarType}
                    onClose={handlePrintModalClose}
                  />
                )}
              </>
            )}
        </>
      }
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  calendarTabsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },
  calendarTabs: {
    flex: 1,
    minWidth: 0,
  },
  autoAssignIndicator: {
    flexShrink: 0,
    height: 18,
    fontSize: 12,
    marginRight: theme.spacing(3),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  autoAssignIndicatorRunning: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  autoAssignIndicatorCompleted: {
    backgroundColor: theme.palette.grey.light,
    color: theme.palette.grey.dark,
  },
  orderBoardContainer: {
    height: "inherit",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: 0,
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  printButton: {
    "&:hover": {
      backgroundColor: theme.palette.grey.light,
    },
  },
}));
